import { type Point } from './cityPlan';
import { type Insets, type Viewport } from './viewport';

/** Where the graph sits in a painted board, in image pixels. */
export interface BoardTransform {
  readonly minX: number;
  readonly minY: number;
  readonly scale: number;
  readonly ox: number;
  readonly oy: number;
}

export interface BoardManifest {
  readonly width: number;
  readonly height: number;
  readonly boards: ReadonlyMap<string, BoardTransform>;
}

export interface PaintedBoard {
  readonly image: CanvasImageSource;
  readonly width: number;
  readonly height: number;
  readonly transform: BoardTransform;
}

export interface PaintedView extends Viewport {
  /** The whole painting's place on the canvas; `scale` is screen px per image px. */
  readonly image: {
    readonly x: number;
    readonly y: number;
    readonly width: number;
    readonly height: number;
    readonly scale: number;
  };
  readonly streetWidth: number;
  readonly plazaRadius: number;
  readonly lampHeight: number;
}

export const EMPTY_BOARD_MANIFEST: BoardManifest = { width: 0, height: 0, boards: new Map() };

const PAINTED_STREET = 26;
const PAINTED_PLAZA = 24;
const PAINTED_LAMP = 60;
const MIN_IMAGE_SCALE = 0.5;
const MIN_AREA_ASPECT = 1;
const GUTTER = 6;
// Table and fog outside the card frame, as a share of the painting; only these may be cropped.
const OUTER_MARGIN: Insets = { top: 0.06, right: 0.01, bottom: 0.03, left: 0.01 };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function finite(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function transformFromJson(raw: unknown): BoardTransform | null {
  if (!isRecord(raw)) return null;
  const [minX, minY, scale, ox, oy] = [raw.minX, raw.minY, raw.scale, raw.ox, raw.oy].map(finite);
  if (minX == null || minY == null || scale == null || ox == null || oy == null) return null;
  return scale > 0 ? { minX, minY, scale, ox, oy } : null;
}

export function parseBoardManifest(raw: unknown): BoardManifest {
  if (!isRecord(raw) || raw.version !== 1 || !isRecord(raw.boards)) return EMPTY_BOARD_MANIFEST;
  const width = finite(raw.width);
  const height = finite(raw.height);
  if (width == null || height == null || width <= 0 || height <= 0) return EMPTY_BOARD_MANIFEST;

  const boards = new Map<string, BoardTransform>();
  for (const [levelId, entry] of Object.entries(raw.boards)) {
    const transform = transformFromJson(entry);
    if (transform) boards.set(levelId, transform);
  }
  return { width, height, boards };
}

/**
 * Fits the painting between the HUD insets so its card frame shows whole, and
 * projects the graph onto its painted plazas. Null when the painting would be
 * shown too small to play on; the procedural board takes over then.
 */
export function fitPaintedBoard(
  board: Pick<PaintedBoard, 'width' | 'height' | 'transform'>,
  width: number,
  height: number,
  safe: Insets,
  fill: 'contain' | 'cover' = 'contain',
): PaintedView | null {
  if (fill === 'cover') return coverPaintedBoard(board, width, height);
  const areaWidth = width - safe.left - safe.right - GUTTER * 2;
  const areaHeight = height - safe.top - safe.bottom - GUTTER * 2;
  if (!(areaWidth > 0 && areaHeight > 0) || areaWidth / areaHeight < MIN_AREA_ASPECT) return null;

  const frameWidth = board.width * (1 - OUTER_MARGIN.left - OUTER_MARGIN.right);
  const frameHeight = board.height * (1 - OUTER_MARGIN.top - OUTER_MARGIN.bottom);
  const scale = Math.min(areaWidth / frameWidth, areaHeight / frameHeight);
  if (scale < MIN_IMAGE_SCALE) return null;

  const x =
    safe.left +
    GUTTER +
    (areaWidth - frameWidth * scale) / 2 -
    board.width * OUTER_MARGIN.left * scale;
  const y =
    safe.top +
    GUTTER +
    (areaHeight - frameHeight * scale) / 2 -
    board.height * OUTER_MARGIN.top * scale;
  return projectedView(board, width, height, x, y, scale);
}

function projectedView(
  board: Pick<PaintedBoard, 'width' | 'height' | 'transform'>,
  width: number,
  height: number,
  x: number,
  y: number,
  scale: number,
): PaintedView {
  const { minX, minY, ox, oy } = board.transform;
  const unit = board.transform.scale * scale;
  const offsetX = x + ox * scale;
  const offsetY = y + oy * scale;

  const project = (position: Point): Point => ({
    x: offsetX + (position.x - minX) * unit,
    y: offsetY + (position.y - minY) * unit,
  });

  return {
    scale: unit,
    scene: {
      minX: minX - offsetX / unit,
      minY: minY - offsetY / unit,
      maxX: minX + (width - offsetX) / unit,
      maxY: minY + (height - offsetY) / unit,
    },
    project,
    projection: { toCanvas: project, unitScale: unit },
    image: { x, y, width: board.width * scale, height: board.height * scale, scale },
    streetWidth: PAINTED_STREET * scale,
    plazaRadius: PAINTED_PLAZA * scale,
    lampHeight: PAINTED_LAMP * scale,
  };
}

// The city map is the whole screen: the painting covers it and the HUD floats on top.
function coverPaintedBoard(
  board: Pick<PaintedBoard, 'width' | 'height' | 'transform'>,
  width: number,
  height: number,
): PaintedView | null {
  if (!(width > 0 && height > 0) || width / height < MIN_AREA_ASPECT) return null;
  const frameWidth = board.width * (1 - OUTER_MARGIN.left - OUTER_MARGIN.right);
  const frameHeight = board.height * (1 - OUTER_MARGIN.top - OUTER_MARGIN.bottom);
  const scale = Math.max(width / frameWidth, height / frameHeight);
  const x = (width - frameWidth * scale) / 2 - board.width * OUTER_MARGIN.left * scale;
  const y = (height - frameHeight * scale) / 2 - board.height * OUTER_MARGIN.top * scale;
  return projectedView(board, width, height, x, y, scale);
}

let manifestPending: Promise<BoardManifest> | null = null;
const boardsPending = new Map<string, Promise<PaintedBoard | null>>();
const boardsLoaded = new Map<string, PaintedBoard>();

function boardsRoot(): string {
  const base = import.meta.env?.BASE_URL;
  return `${typeof base === 'string' && base.length > 0 ? base : '/'}boards`;
}

async function fetchManifest(): Promise<BoardManifest> {
  const response = await fetch(`${boardsRoot()}/manifest.json`);
  return response.ok ? parseBoardManifest(await response.json()) : EMPTY_BOARD_MANIFEST;
}

function ensureManifest(): Promise<BoardManifest> {
  manifestPending ??= fetchManifest().catch(() => EMPTY_BOARD_MANIFEST);
  return manifestPending;
}

function loadImage(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image.naturalWidth > 0 ? image : null);
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

async function fetchBoard(levelId: string): Promise<PaintedBoard | null> {
  const manifest = await ensureManifest();
  const transform = manifest.boards.get(levelId);
  if (!transform) return null;
  const image = await loadImage(`${boardsRoot()}/${levelId.replace(':', '_')}.webp`);
  if (!image) return null;
  const board = { image, width: manifest.width, height: manifest.height, transform };
  boardsLoaded.set(levelId, board);
  return board;
}

/** Loads the level's painted board once; null when it has none. */
export function requestPaintedBoard(levelId: string): Promise<PaintedBoard | null> {
  if (typeof Image === 'undefined' || typeof fetch !== 'function') return Promise.resolve(null);
  let pending = boardsPending.get(levelId);
  if (!pending) {
    pending = fetchBoard(levelId).catch(() => null);
    boardsPending.set(levelId, pending);
  }
  return pending;
}

export function paintedBoard(levelId: string): PaintedBoard | null {
  return boardsLoaded.get(levelId) ?? null;
}

export function resetPaintedBoards(): void {
  manifestPending = null;
  boardsPending.clear();
  boardsLoaded.clear();
}

function edgeTone(image: CanvasImageSource): readonly number[] {
  const probe = document.createElement('canvas');
  probe.width = 8;
  probe.height = 8;
  const ctx = probe.getContext('2d', { willReadFrequently: true });
  if (!ctx) return [16, 19, 28];
  ctx.drawImage(image, 0, 0, 8, 8);
  const { data } = ctx.getImageData(0, 0, 8, 8);
  const sum = [0, 0, 0];
  let count = 0;
  for (let i = 0; i < 64; i++) {
    const x = i % 8;
    const y = Math.floor(i / 8);
    if (x > 0 && x < 7 && y > 0 && y < 7) continue;
    for (let c = 0; c < 3; c++) sum[c]! += data[i * 4 + c]!;
    count++;
  }
  return sum.map((v) => Math.round(v / count));
}

const FEATHER = 0.05;

/** The painting on a table of its own edge tone, its borders fading into it. */
export function bakePaintedLayer(
  board: PaintedBoard,
  view: PaintedView,
  width: number,
  height: number,
  pixelRatio: number,
): HTMLCanvasElement {
  const layer = document.createElement('canvas');
  layer.width = Math.max(1, Math.round(width * pixelRatio));
  layer.height = Math.max(1, Math.round(height * pixelRatio));
  const ctx = layer.getContext('2d');
  if (!ctx) return layer;

  ctx.fillStyle = `rgb(${edgeTone(board.image).join(', ')})`;
  ctx.fillRect(0, 0, layer.width, layer.height);

  const x = Math.round(view.image.x * pixelRatio);
  const y = Math.round(view.image.y * pixelRatio);
  const w = Math.round(view.image.width * pixelRatio);
  const h = Math.round(view.image.height * pixelRatio);
  const left = Math.max(0, -x);
  const top = Math.max(0, -y);
  const right = Math.min(w, layer.width - x);
  const bottom = Math.min(h, layer.height - y);
  if (!(right > left && bottom > top)) return layer;

  const painting = document.createElement('canvas');
  painting.width = right - left;
  painting.height = bottom - top;
  const paint = painting.getContext('2d');
  if (!paint) return layer;
  paint.imageSmoothingQuality = 'high';
  paint.drawImage(board.image, -left, -top, w, h);

  const feather = Math.round(Math.min(w, h) * FEATHER);
  paint.globalCompositeOperation = 'destination-out';
  const fade = (x0: number, y0: number, x1: number, y1: number): void => {
    const gradient = paint.createLinearGradient(x0 - left, y0 - top, x1 - left, y1 - top);
    gradient.addColorStop(0, 'rgba(0, 0, 0, 1)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
    paint.fillStyle = gradient;
  };
  fade(0, 0, feather, 0);
  paint.fillRect(-left, -top, feather, h);
  fade(w, 0, w - feather, 0);
  paint.fillRect(w - feather - left, -top, feather, h);
  fade(0, 0, 0, feather);
  paint.fillRect(-left, -top, w, feather);
  fade(0, h, 0, h - feather);
  paint.fillRect(-left, h - feather - top, w, feather);

  ctx.drawImage(painting, x + left, y + top);
  return layer;
}
